<template>
	<el-form-item label="Поиск по названию узла или по названию группы узлов" label-position="top">
		<el-select
			v-model="foundJobsSelect"					
			filterable
			remote
			reserve-keyword
			clearable
			placeholder="Узел (компонет) или название группы"
			:remote-method="searchJobsAsync"
			v-loading="foundJobsSelectLoading"
			@change="onJobComponentSearchResultsChange"
		>
			<template #empty>
				Ничего не найдено ☹️
			</template>
			<template #footer>
				<kbd class="DocSearch-Commands-Key" style="background: var(--docsearch-key-gradient);">
					<svg width="15" height="15" aria-label="Enter key" role="img">
						<g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2">
							<path d="M12 3.53088v3c0 1-1 2-2 2H4M7 11.53088l-3-3 3-3"></path>
						</g>
					</svg>
				</kbd>
			</template>
			<el-option
				v-for="item in foundJobsSelectItems"
				:key="item.id"
				:label="item.name"
				:value="item.id"
			/>
		</el-select>
	</el-form-item>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { ElNotification, ElTag } from 'element-plus';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import ServiceJobModel from '@/models/ServiceJobModel';
import EmployeeModel from '@/models/EmployeeModel';
import OrderJobViewModel from '@/ViewModels/OrderJobViewModel';
import ServiceJobComponentModel from '@/models/ServiceJobComponentModel';
import ServiceJobOperationModel from '@/models/ServiceJobOperationModel';
import UserModel from '@/models/UserModel';
import JobsApiHelper from '@/helpers/api/JobsApiHelper';
import JobComponentsApiHelper from '@/helpers/api/JobComponentsApiHelper';

@Options({	
	components: {
		ElTag
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		query: {
			type: String,
			default: ''
		}
	},
	emits: ['picked'],
})
export default class OrderJobsSearchComponent extends Vue {	
	foundJobsSelect: ServiceJobComponentModel | null = null;
	foundJobsSelectItems: Array<ServiceJobComponentModel> = [];
	foundJobsSelectLoading = false;

	dialogJobComponentOperationsVisible = false;
	componentOperations: Array<ServiceJobOperationModel> = [];
	selectedComponentOperationIds: Array<number> = [];

	/** constants */
	foundJobsSelectHttpClient = axiosHelper.createInstance(store.getters.jwt);
	jobComponentsApiHelper = new JobComponentsApiHelper(store.getters.jwt);

	async searchJobsAsync(query: string) : Promise<void> {
		if (!query?.length) {
			return;
		}		
		
		try {
			this.foundJobsSelectLoading = true;
			const results = await this.foundJobsSelectHttpClient.get(`/api/servicejobs/search-by-component`, { params: { query } });
			if (!results.data?.length) {
				this.foundJobsSelectItems = [];
				return;
			}
			const foundJobsComponents = results.data.map((_: any) => new ServiceJobModel(_).component);
			this.foundJobsSelectItems = foundJobsComponents as Array<ServiceJobComponentModel>;
		} catch (e) {
			console.error(`Unable to search jobs by job-component name '${query}'`, e);
		} finally {
			this.foundJobsSelectLoading = false;
		}
	}

	/** fires when user picked a particular component in search-results window */
	async onJobComponentSearchResultsChange(componentId: number) {		
		const componentDefaultJob = await this.jobComponentsApiHelper.getDefaultJobAsync(componentId);		

		if (!componentDefaultJob?.id) {
			ElNotification({
				message: `Ошибка добавления работы в заказ-наряд`,
				type: 'error',
			});
			console.error(`Unable to add service-job to order: can't get component's #${componentId} default job`);
		} else {
			const orderJob = await this.createOrderJobViewModelAsync(componentDefaultJob);
			this.foundJobsSelectItems = [];
			this.foundJobsSelect = null;
			this.dialogJobComponentOperationsVisible = true;

			this.$emit('picked', orderJob);
		}
	}

	// async onApplyOperationsClick() {
	// 	if (!this.selectedComponentOperationIds.length) {
	// 		return;
	// 	}
	// 	const componentId = this.selectedComponentId;
	// 	const jobs = await this.getJobsByComponentAndOperationsAsync(componentId, this.selectedComponentOperationIds);

	// 	if (!jobs.length) {
	// 		const conjunctedJobNoun = this.selectedComponentOperationIds.length == 1 ? 'работы' : 'работ';
	// 		ElNotification({
	// 			message: `Ошибка добавления ${conjunctedJobNoun} в заказ-наряд`,
	// 			type: 'error',
	// 		});
	// 		return;
	// 	}

	// 	const orderJobs: Array<OrderJobViewModel> = [];


	// 	const newJobTasks = jobs.map(async serviceJob => {	
	// 		const orderJobViewModel = await this.createOrderJobViewModelAsync(serviceJob);
	// 		orderJobs.push(orderJobViewModel);
	// 	});

	// 	await Promise.all(newJobTasks);		

	// 	this.selectedComponent = '';
	// 	this.selectedComponentId = 0;
	// 	this.selectedComponentOperationIds = [];
	// 	this.componentOperations = [];
	// 	this.foundJobsSelectItems = [];
	// 	this.foundJobsSelect = null;
	// 	this.dialogJobComponentOperationsVisible = false;

	// 	this.$emit('picked', orderJobs);
	// }

	/** get list of available operations (service-job-operations) are related to a component */
	async getOperationsAsync(componentId: number) {
		const jobsApiHelper = new JobsApiHelper();
		const jobs = await jobsApiHelper.getJobsByComponentAsync(store.getters.jwt, componentId);
		if (!jobs.length) {
			return [];
		}

		return jobs.map(_ => _.operation);
	}	

	/** get list of jobs are related to component and have definite operations */
	async getJobsByComponentAndOperationsAsync(componentId: number, operationIds: Array<number>) {
		const operationsString = operationIds.join(',');
		try {
			const findJobsHttpClient = axiosHelper.createInstance(store.getters.jwt);
			const jobResponse = await findJobsHttpClient.get(`/api/servicejobs/jobs/${componentId}?operations=${operationsString}`);
			const jobs: Array<ServiceJobModel> = jobResponse.data.map((_: any) => new ServiceJobModel(_));
			return jobs;
		} catch(e) {
			console.error(`Unable to find jobs related to component #${componentId} and operations [${operationsString}]`, e);
			return [];
		}
	}

	async createOrderJobViewModelAsync(serviceJob: ServiceJobModel) : Promise<OrderJobViewModel> {	
		const componentId = serviceJob.component!.id;
		const manager = new EmployeeModel((store.getters.user as UserModel).employee!);
		const orderJobViewModel = new OrderJobViewModel({
			manager,
			serviceJob,
			estimationHrs: 1
		});
		orderJobViewModel.availableOperations = await this.getOperationsAsync(componentId);
		return orderJobViewModel;
	}
}
</script>